import { Box } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";
import { dateFormatter } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import { useWorkspaceContext } from "../../../context/workspaceContext";
import { useUrl } from "../../../hooks/urlEncoding";
import { Link, useLocation } from "react-router-dom";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import { SecureCallStatus } from "../../../services/Schemas/SecureCalls";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import { routerPaths } from "../../../constants/Paths";
import UserLink from "../common/UserLink";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { WorkspaceListColumnFilterable, WorkspaceListColumnSortable } from "../../../services/Endpoints/workspaces";
import { AudioPlayerProvider } from "../../../context/audioPlayerContext";
import {
  OTPListColumnSortable,
  OTPListRequest,
} from "../../../services/Endpoints/otp-calls/OTPCallsListEndpoints";
import useUrlSort from "../../../hooks/useUrlSort";

export interface ISecureCall {
  id: number;
  number: string;
  created_at: string;
  status: ReactNode | string;
  actual_duration: number;
  actual_cost: number;
  sent_time: string | null;
  workspace_id: ReactNode | number;
}

const secureCallStatus = {
  [SecureCallStatus.CREATED]: "ایجاد شده",
  [SecureCallStatus.WAITING_FOR_SEND]: "در انتظار ارسال",
  [SecureCallStatus.SENDING]: "در حال ارسال",
  [SecureCallStatus.BLOCKED]: "مسدود شده",
  [SecureCallStatus.FAILED]: "ناموفق",
  [SecureCallStatus.ANSWERED]: "پاسخ داده شده",
  [SecureCallStatus.BUSY]: "اشغال",
  [SecureCallStatus.REJECTED]: "رد شده",
  [SecureCallStatus.NO_ANSWER]: "بی‌پاسخ",
  [SecureCallStatus.DEVICE_OFF]: "دستگاه خاموش است",
  [SecureCallStatus.INVALID_NUMBER]: "شماره نامعتبر",
  [SecureCallStatus.UNREACHABLE]: "غیرقابل دسترسی",
  [SecureCallStatus.CANCELED]: "لغو شده",
};

export interface ISupportTabsComponent {
  setHasSecureCall: (payload: boolean) => void;
}

export default function OTPCallsTable() {
  const { parseQueryString, addToQueryStringUrl, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const { lang } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [data, setData] = useState<ISecureCall[] | []>([]);
  const [total, setTotal] = useState(0);
  const { workspace } = useWorkspaceContext();
  const [waiting, setWaiting] = useState(true);
  const [callsReload, setCallsReload] = useState(false);
  const location = useLocation();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const getData = async () => {
    try {
      setWaiting(true);
      const otpCallListRequest = new OTPListRequest(protectedInstance);
      const resp = await otpCallListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const secureCalls = resp.data.otp_calls.map(
        ({
          id,
          workspace_id,
          number,
          created_at,
          status,
          actual_duration,
          actual_cost,
          sent_time,
        }) => ({
          id,
          number,
          created_at,
          sent_time,
          workspace_id: (
            <UserLink
              disabled={!can(AccessEnums.AdminWorkspacesList)}
              user={{ id: workspace_id }}
              link={addToQueryStringUrl(routerPaths.WORKSPACES, {
                filters: {
                  [WorkspaceListColumnFilterable.ID]: {
                    column: WorkspaceListColumnFilterable.ID,
                    value: workspace_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
                sorts: {
                  [WorkspaceListColumnSortable.ID]: {
                    column: WorkspaceListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          status: secureCallStatus[status],
          actual_duration,
          actual_cost,
        })
      );
      setData(secureCalls);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [workspace, callsReload, page, rowsPerPage, location.search]);

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 150,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "created_at",
      label: "زمان ایجاد",
      minWidth: 150,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "number",
      label: "شماره مخاطب",
      minWidth: 150,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "workspace_id",
      label: "شناسه تخته",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "sent_time",
      label: "زمان ارسال",
      minWidth: 150,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "status",
      label: "وضعیت تماس",
      minWidth: 120,
      align: "center",
      FilterComponent: SelectionFilter,
      options: secureCallStatus,
    },
    {
      key: "actual_duration",
      label: "مدت‌‌ مکالمه",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "actual_cost",
      label: "هزینه‌ تماس",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
    },
  ];
  const { filtersSchema, columns } = useTableSchema(_columns);
  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <AudioPlayerProvider>
      <Box
        sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}
      >
        <CustomTable
          reloadAction={() => setCallsReload(!callsReload)}
          hasWaiting={waiting}
          hasSearchFilter
          hasBottomPagination
          hasTopPagination
          columns={columns}
          count={total}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rows={data}
          rowsPerPage={rowsPerPage}
          filtersSchema={filtersSchema}
          onApplySorts={applyUrlSorts}
          sorts={sorts}
        />
      </Box>
    </AudioPlayerProvider>
  );
}
